import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { cn } from "../lib/utils";

const FALLBACK_ZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Moscow",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Tokyo",
  "Australia/Sydney",
];

function getTimezones() {
  let list = [];
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      list = Intl.supportedValuesOf("timeZone");
    }
  } catch (e) {
    // fall through
  }
  // Merge curated modern IANA names first (some engines expose legacy aliases,
  // e.g. Asia/Calcutta instead of Asia/Kolkata), so search-by-city always works.
  const merged = [];
  const seen = new Set();
  for (const tz of ["UTC", ...FALLBACK_ZONES, ...list]) {
    if (tz && !seen.has(tz)) {
      seen.add(tz);
      merged.push(tz);
    }
  }
  return merged;
}

function tzOffsetLabel(tz) {
  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    });
    const part = dtf.formatToParts(new Date()).find((p) => p.type === "timeZoneName");
    return part ? part.value : "";
  } catch (e) {
    return "";
  }
}

export function TimezoneSelect({ value, onChange, testId, className, disabled }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const zones = useMemo(getTimezones, []);
  const current = value || "UTC";
  const q = search.toLowerCase().trim();
  const filtered = useMemo(
    () => (q ? zones.filter((tz) => tz.toLowerCase().replace(/[_/]/g, " ").includes(q)) : zones),
    [zones, q]
  );

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(""); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", className)}
          data-testid={testId}
        >
          <span className="truncate">
            {current}
            <span className="ml-2 text-xs text-slate-400">{tzOffsetLabel(current)}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search timezone…"
            value={search}
            onValueChange={setSearch}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            <CommandEmpty>No timezone found.</CommandEmpty>
            <CommandGroup>
              {filtered.map((tz) => (
                <CommandItem
                  key={tz}
                  value={tz}
                  onSelect={() => {
                    onChange(tz);
                    setOpen(false);
                  }}
                  data-testid={testId ? `${testId}-option-${tz}` : undefined}
                >
                  <Check
                    className={cn("mr-2 h-4 w-4", current === tz ? "opacity-100" : "opacity-0")}
                  />
                  <span className="flex-1 truncate">{tz}</span>
                  <span className="ml-2 text-xs text-slate-400">{tzOffsetLabel(tz)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
